import { useCallback, useRef } from 'react';

/**
 * For a Radix dialog built from `DialogPrimitive.Content` directly (not the shared components/ui/dialog.tsx wrappers,
 * which already do this): Radix only returns focus to a <Dialog.Trigger>, so a controlled dialog drops focus to <body>
 * (WCAG 2.4.3).
 *
 *   const { onCloseAutoFocus } = useOpenerFocus(open);
 *   <Dialog open={open}> <Content onCloseAutoFocus={onCloseAutoFocus}>
 *
 * The opener is read while rendering the first open state, before anything inside the dialog takes focus.
 */
export function useOpenerFocus(open: boolean) {
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (open !== wasOpenRef.current) {
    wasOpenRef.current = open;
    if (open && typeof document !== 'undefined') openerRef.current = document.activeElement as HTMLElement | null;
  }
  const onCloseAutoFocus = useCallback((event: Event) => {
    if (event.defaultPrevented) return;
    const opener = openerRef.current;
    if (opener && opener !== document.body && opener.isConnected) {
      event.preventDefault();
      opener.focus();
    }
  }, []);
  return { onCloseAutoFocus };
}
